import { APP_VERSION, BUILD_TIMESTAMP } from "@/lib/version";

export type BugReportKind = "manual" | "runtime_error";

export interface ClientErrorBufferEntry {
  message: string;
  source: "error" | "unhandledrejection" | "manual";
  stack?: string;
  created_at?: string;
}

export interface BugReportContext {
  route_path: string;
  current_url: string;
  query: Record<string, string>;
  build_version: string;
  build_time: string;
  browser: {
    user_agent: string;
    language: string;
    online: boolean;
  };
  viewport: {
    width: number;
    height: number;
    device_pixel_ratio: number;
  };
  tenant_id?: string;
  clinic_id?: string | null;
  user_id?: string;
  sentry_event_id?: string | null;
  system_error_log_id?: string | null;
  recent_errors: ClientErrorBufferEntry[];
}

const MAX_RECENT_ERRORS = 10;
const ALLOWED_QUERY_KEYS = new Set([
  "id",
  "ticketId",
  "ticketid",
  "tenantId",
  "tenantid",
  "clinicId",
  "clinicid",
  "patientId",
  "patientid",
  "appointmentId",
  "appointmentid",
  "visitId",
  "visitid",
  "billId",
  "billid",
  "caseId",
  "caseid",
  "itemId",
  "itemid",
]);

const clientErrorBuffer: ClientErrorBufferEntry[] = [];

export function sanitizeBugReportText(value: string): string {
  return value
    .replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, "[EMAIL]")
    .replace(/\+?\d[\d\s().-]{7,}\d/g, "[PHONE]")
    .replace(/\b(?:token|secret|password|authorization|api[_-]?key)(?:[:=\s-]+)[^\s,;]+/gi, "[REDACTED]");
}

function getRuntimeBuildVersion() {
  return (globalThis as { __BUILD_DATE__?: string }).__BUILD_DATE__ || APP_VERSION;
}

function getRuntimeBuildTime() {
  return (globalThis as { __BUILD_TIME__?: string }).__BUILD_TIME__ || BUILD_TIMESTAMP;
}

function getSafeQueryParams(search: string): Record<string, string> {
  const params = new URLSearchParams(search);
  const safeParams: Record<string, string> = {};

  params.forEach((value, key) => {
    if (ALLOWED_QUERY_KEYS.has(key)) {
      safeParams[key] = sanitizeBugReportText(value);
    }
  });

  return safeParams;
}

export function addClientErrorToBuffer(entry: ClientErrorBufferEntry) {
  clientErrorBuffer.push({
    ...entry,
    message: sanitizeBugReportText(entry.message).slice(0, 500),
    stack: entry.stack ? sanitizeBugReportText(entry.stack).slice(0, 1500) : undefined,
    created_at: entry.created_at || new Date().toISOString(),
  });

  while (clientErrorBuffer.length > MAX_RECENT_ERRORS) {
    clientErrorBuffer.shift();
  }
}

export function clearClientErrorBuffer() {
  clientErrorBuffer.splice(0, clientErrorBuffer.length);
}

export function initClientErrorBuffer() {
  if (typeof window === "undefined") return;

  window.addEventListener("error", (event) => {
    addClientErrorToBuffer({
      message: event.message || "Unhandled window error",
      source: "error",
      stack: event.error?.stack,
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    addClientErrorToBuffer({
      message: reason?.message || String(reason || "Unhandled promise rejection"),
      source: "unhandledrejection",
      stack: reason?.stack,
    });
  });
}

export function buildBugReportContext(input: {
  tenantId?: string;
  clinicId?: string | null;
  userId?: string;
  sentryEventId?: string | null;
  systemErrorLogId?: string | null;
} = {}): BugReportContext {
  const currentUrl = new URL(window.location.href);
  currentUrl.search = "";
  currentUrl.hash = "";

  return {
    route_path: window.location.pathname,
    current_url: currentUrl.toString(),
    query: getSafeQueryParams(window.location.search),
    build_version: getRuntimeBuildVersion(),
    build_time: getRuntimeBuildTime(),
    browser: {
      user_agent: navigator.userAgent,
      language: navigator.language,
      online: navigator.onLine,
    },
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight,
      device_pixel_ratio: window.devicePixelRatio || 1,
    },
    tenant_id: input.tenantId,
    clinic_id: input.clinicId,
    user_id: input.userId,
    sentry_event_id: input.sentryEventId,
    system_error_log_id: input.systemErrorLogId,
    recent_errors: [...clientErrorBuffer],
  };
}
